import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  createTake, listTakes, uploadVocal, analyzeTake, releaseTake,
  deleteTake, listBeats, trendData, submitSigningDay, aiQuota,
} from '../lib/practiceApi';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { SIGNING_DAY, SIGNING_DAY_LABEL } from '../config/signingDay';
import { analyzeBeat } from '../lib/bpm';

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, '0')}`;

const PRE_LAUNCH = SIGNING_DAY.getTime() > Date.now();

const BG = '#0a0a0b';
const PANEL = '#100f0d';
const BORDER = '#2a2620';
const TEXT = '#e9e5dc';
const MUTE = '#8b857a';
const MONO = "'JetBrains Mono','SF Mono',Menlo,Consolas,monospace";
const BARCODE_URL = 'https://barcode.effortlessmetaphor.org';
const AXES = ['flow', 'cadence', 'delivery', 'clarity'];

const ACCENTS = ['#C9A84C', '#39FF14', '#00E5FF', '#FF3DCB', '#8B5CFF', '#FF3B30', '#FFE000'];
const BANNERS = [
  { id: 'dorm', label: 'Dorm', val: '/dorm.jpg?v=1' },
  { id: 'neon', label: 'Neon', val: 'linear-gradient(135deg,#1a0b2e,#3b1d5e 50%,#08203b)' },
  { id: 'noir', label: 'Noir', val: 'linear-gradient(180deg,#171511,#0a0a0b)' },
];

const TUTORIAL = [
  { n: '01', t: 'Pick a beat', d: 'Load your own track to rap over — or go a cappella.' },
  { n: '02', t: 'Write your verse', d: 'Drop bars in the BARCODE notebook — rhyme + flow tools built in.' },
  { n: '03', t: 'Record over it', d: 'Hit record; the beat plays in your headphones while you lay your vocal.' },
  { n: '04', t: 'Feedback + lock in', d: 'Get instant AI feedback, then lock in the track you drop on Signing Day.' },
];

export default function PracticeRoomPage() {
  const { token } = useAuth();
  const [takes, setTakes] = useState([]);
  const [, setBeats] = useState([]);
  const [lyrics, setLyrics] = useState('');
  const [licenseOk, setLicenseOk] = useState(false);
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState('');
  const [beatUrl, setBeatUrl] = useState('');
  const [beatName, setBeatName] = useState('');
  const [beatBpm, setBeatBpm] = useState(null);
  const [bpmBusy, setBpmBusy] = useState(false);
  const [beatSegments, setBeatSegments] = useState([]);
  const [beatDur, setBeatDur] = useState(0);
  const [showTut, setShowTut] = useState(() => !localStorage.getItem('tw_room_tut_seen'));
  const [showCustomize, setShowCustomize] = useState(false);

  // customization (persisted)
  const [accent, setAccent] = useState(() => localStorage.getItem('tw_room_accent') || '#C9A84C');
  const [banner, setBanner] = useState(() => localStorage.getItem('tw_room_banner') || '/dorm.jpg?v=1');
  useEffect(() => { localStorage.setItem('tw_room_accent', accent); }, [accent]);
  useEffect(() => { try { localStorage.setItem('tw_room_banner', banner); } catch { /* too big */ } }, [banner]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const beatAudioRef = useRef(null);
  const beatInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const sBeat = useRef(null);
  const sWrite = useRef(null);
  const sRec = useRef(null);
  const sFb = useRef(null);

  // accent-driven styles
  const A = accent;
  const label = { fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTE };
  const panel = { background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 4 };
  const accentBtn = { fontFamily: MONO, fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#000', background: A, border: 'none', borderRadius: 3, padding: '12px 18px', cursor: 'pointer', fontWeight: 700 };
  const ghostBtn = { fontFamily: MONO, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: A, background: 'transparent', border: `1px solid ${A}88`, borderRadius: 3, padding: '7px 12px', cursor: 'pointer' };

  // AI demo meter (20-min window for the AI coach; the rest of the room is free)
  const [aiQ, setAiQ] = useState(null);      // { unlocked, started, remaining_seconds, locked }
  const [aiRemaining, setAiRemaining] = useState(null);
  const [showWall, setShowWall] = useState(false);
  const aiDeadlineRef = useRef(null);

  const loadQuota = useCallback(async () => {
    try {
      const q = await aiQuota(token, 'trapworld');
      setAiQ(q);
      if (q.unlocked || !q.started || q.remaining_seconds == null) {
        aiDeadlineRef.current = null;
        setAiRemaining(q.unlocked ? null : q.remaining_seconds);
      } else {
        aiDeadlineRef.current = Date.now() + q.remaining_seconds * 1000;
        setAiRemaining(q.remaining_seconds);
      }
    } catch { /* meter unavailable — fail open in UI, server still enforces */ }
  }, [token]);

  // Live tick for the countdown chip.
  useEffect(() => {
    if (!aiQ || aiQ.unlocked || !aiQ.started) return undefined;
    const id = setInterval(() => {
      if (aiDeadlineRef.current == null) return;
      const left = Math.max(0, Math.round((aiDeadlineRef.current - Date.now()) / 1000));
      setAiRemaining(left);
    }, 1000);
    return () => clearInterval(id);
  }, [aiQ]);

  const aiLocked = !!aiQ && !aiQ.unlocked && aiQ.started && (aiRemaining != null && aiRemaining <= 0);

  const refresh = useCallback(async () => {
    setTakes(await listTakes(token));
    try { setBeats(await listBeats(token)); } catch { /* none yet */ }
    loadQuota();
  }, [token, loadQuota]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin.indexOf('barcode') === -1 && e.origin.indexOf('effortlessmetaphor') === -1) return;
      const d = e.data || {};
      if (d.type === 'barcode:lyrics' && typeof d.text === 'string') setLyrics(d.text);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismissTut = () => { localStorage.setItem('tw_room_tut_seen', '1'); setShowTut(false); };
  const goto = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // --- beat loading (robust) ---
  const onBeatFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (beatUrl) URL.revokeObjectURL(beatUrl);
    setBeatUrl(URL.createObjectURL(f));
    setBeatName(f.name);
    setBeatBpm(null); setBeatSegments([]); setBeatDur(0);
    setBpmBusy(true);
    try {
      const a = await analyzeBeat(await f.arrayBuffer());
      setBeatBpm(a.bpm); setBeatSegments(a.segments); setBeatDur(a.duration);
    } catch { /* ignore */ }
    finally { setBpmBusy(false); }
  };
  const clearBeat = () => { if (beatUrl) URL.revokeObjectURL(beatUrl); setBeatUrl(''); setBeatName(''); setBeatBpm(null); setBeatSegments([]); setBeatDur(0); };
  const beatLoaded = !!beatName;

  // --- banner upload (downscale -> dataURL, persisted) ---
  const onBannerFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1400 / img.width);
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      try { setBanner(c.toDataURL('image/jpeg', 0.82)); } catch { /* ignore */ }
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(f);
  };

  const startRecording = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { alert('Mic access denied. Allow the microphone to record.'); return; }
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    mr.ondataavailable = (ev) => chunksRef.current.push(ev.data);
    mr.onstop = () => {
      setBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
      stream.getTracks().forEach((t) => t.stop());
      if (beatAudioRef.current) beatAudioRef.current.pause();
    };
    mediaRef.current = mr;
    if (beatUrl && beatAudioRef.current) {
      beatAudioRef.current.currentTime = 0;
      beatAudioRef.current.play().catch(() => {});
    }
    mr.start();
    setRecording(true);
  };
  const stopRecording = () => { mediaRef.current?.stop(); beatAudioRef.current?.pause(); setRecording(false); };

  const submit = async () => {
    if (!licenseOk) { alert('Please accept the content license to continue.'); return; }
    if (!blob) { alert('Record a take first (③ The Booth).'); goto(sRec); return; }
    try {
      setBusy('Saving');
      const take = await createTake(token, { lyrics, beat_ref: beatName ? `local:${beatName}` : 'acappella', license_accepted: true });
      setBusy('Uploading');
      await uploadVocal(token, take.take_id, blob);
      // The take is saved either way. AI feedback is the metered part: if the
      // 20-min demo window is spent, skip the AI call and show the pay wall —
      // the take itself is kept, the artist just doesn't get the AI coach.
      if (aiLocked) {
        setShowWall(true);
      } else {
        setBusy('Analyzing');
        try {
          await analyzeTake(token, take.take_id);
        } catch (err) {
          if (err?.response?.status === 402) setShowWall(true);
          /* other failures are shown in the take list */
        }
        loadQuota();
      }
      setBlob(null); setLyrics('');
    } catch (e) {
      alert('Something went wrong saving your take. Try again.');
    } finally { setBusy(''); refresh(); }
  };

  const onRelease = async (id) => {
    if (!window.confirm('Release this take to the public Arena? Other artists will judge it.')) return;
    await releaseTake(token, id); refresh();
  };
  const onLockIn = async (id) => {
    if (!window.confirm('Lock this in as your Signing Day track? It stays private until Signing Day, when it drops into the Arena. (You can change your pick anytime before then.)')) return;
    await submitSigningDay(token, id); refresh();
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete this take permanently?')) return;
    await deleteTake(token, id); refresh();
  };

  const points = trendData(takes);
  const tile = { ...panel, flex: '1 1 130px', padding: '12px 14px', cursor: 'pointer', textAlign: 'left' };
  const heroBg = banner.startsWith('linear-gradient')
    ? { background: banner }
    : { backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center' };

  return (
    <div style={{ background: BG, minHeight: '100vh', color: TEXT, fontFamily: MONO }}>
      <audio ref={beatAudioRef} src={beatUrl || undefined} loop preload="auto" />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px 64px' }}>
        {/* Dorm hero */}
        <section style={{ position: 'relative', height: 280, borderRadius: 6, overflow: 'hidden', marginBottom: 16, border: `1px solid ${BORDER}`, ...heroBg }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,10,11,0.12) 0%, rgba(10,10,11,0.5) 55%, rgba(10,10,11,0.96) 100%)' }} />
          <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 8 }}>
            {aiQ && (
              <button
                onClick={() => aiLocked && setShowWall(true)}
                title="AI coach demo time"
                style={{ ...ghostBtn, cursor: aiLocked ? 'pointer' : 'default',
                  background: 'rgba(0,0,0,0.45)',
                  color: aiLocked ? '#FF3B30' : A,
                  borderColor: aiLocked ? '#FF3B3088' : `${A}88` }}
              >
                {aiQ.unlocked ? 'AI ✦ unlimited'
                  : aiLocked ? 'AI coach · locked'
                  : `AI coach · ${fmtTime(aiRemaining != null ? aiRemaining : (aiQ.remaining_seconds || 0))}${aiQ.started ? ' left' : ' free'}`}
              </button>
            )}
            <button onClick={() => setShowCustomize((v) => !v)} style={{ ...ghostBtn, background: 'rgba(0,0,0,0.45)' }}>✦ Customize</button>
            <button onClick={() => setShowTut(true)} style={{ ...ghostBtn, background: 'rgba(0,0,0,0.45)' }}>How it works</button>
          </div>
          <div style={{ position: 'absolute', left: 22, right: 22, bottom: 18 }}>
            <h1 style={{ fontFamily: MONO, fontSize: 34, letterSpacing: '0.16em', color: A, margin: 0, fontWeight: 700, textShadow: '0 2px 14px rgba(0,0,0,0.85)' }}>YOUR&nbsp;ROOM</h1>
            <div style={{ ...label, marginTop: 7, color: '#ddd7c8', textShadow: '0 1px 8px #000' }}>
              Private · outfitted with everything you need · train for Signing Day
            </div>
          </div>
        </section>

        {/* Customize panel */}
        {showCustomize && (
          <section style={{ ...panel, padding: 18, marginBottom: 18, borderColor: `${A}66` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ ...label, color: A, fontSize: 12 }}>Customize your room</div>
              <button onClick={() => setShowCustomize(false)} style={{ ...ghostBtn, color: MUTE, borderColor: BORDER }}>Done ✕</button>
            </div>

            <div style={{ ...label, marginBottom: 8 }}>Banner</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              {BANNERS.map((b) => (
                <button key={b.id} onClick={() => setBanner(b.val)}
                  style={{ width: 96, height: 54, borderRadius: 4, cursor: 'pointer',
                    border: `2px solid ${banner === b.val ? A : BORDER}`,
                    ...(b.val.startsWith('linear-gradient') ? { background: b.val } : { backgroundImage: `url(${b.val})`, backgroundSize: 'cover', backgroundPosition: 'center' }) }}
                  title={b.label} />
              ))}
              <button onClick={() => bannerInputRef.current?.click()} style={ghostBtn}>Upload your own ↑</button>
              <input ref={bannerInputRef} type="file" accept="image/*" onChange={onBannerFile} style={{ display: 'none' }} />
            </div>

            <div style={{ ...label, marginBottom: 8 }}>Theme</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {ACCENTS.map((c) => (
                <button key={c} onClick={() => setAccent(c)}
                  style={{ width: 30, height: 30, borderRadius: '50%', background: c, cursor: 'pointer',
                    border: accent === c ? '3px solid #fff' : '2px solid rgba(255,255,255,0.2)' }} title={c} />
              ))}
            </div>
          </section>
        )}

        {/* Tutorial */}
        {showTut && (
          <section style={{ ...panel, padding: 18, marginBottom: 18, borderColor: `${A}66`, background: `linear-gradient(180deg, ${A}12, transparent)` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ ...label, color: A, fontSize: 12 }}>Welcome to your room — here's the run</div>
              <button onClick={dismissTut} style={{ ...ghostBtn, color: MUTE, borderColor: BORDER }}>Got it ✕</button>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {TUTORIAL.map((s) => (
                <div key={s.n} style={{ flex: '1 1 200px', background: '#0c0b09', border: `1px solid ${BORDER}`, borderRadius: 3, padding: 12 }}>
                  <div style={{ fontFamily: MONO, color: A, fontSize: 18, fontWeight: 700 }}>{s.n}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>{s.t}</div>
                  <div style={{ fontSize: 12, color: MUTE, marginTop: 4, lineHeight: 1.45 }}>{s.d}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Station tiles */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
          {[['① Beat', 'Load a beat / a cappella', sBeat], ['② Write', 'BARCODE notebook', sWrite], ['③ Record', 'The booth · mini-DAW', sRec], ['④ Feedback', 'Instant AI + lock in', sFb]].map(([t, d, r]) => (
            <div key={t} style={tile} onClick={() => goto(r)}>
              <div style={{ ...label, color: A }}>{t}</div>
              <div style={{ fontSize: 12, color: MUTE, marginTop: 4 }}>{d}</div>
            </div>
          ))}
        </div>

        {/* Signing Day strip */}
        {PRE_LAUNCH && (
          <section style={{ ...panel, padding: 16, marginBottom: 22, borderColor: `${A}66`, background: `linear-gradient(180deg, ${A}12, transparent)` }}>
            <div style={{ ...label, color: A }}>Training Camp · Signing Day {SIGNING_DAY_LABEL}</div>
            <p style={{ color: TEXT, fontSize: 13, marginTop: 8, maxWidth: 660, lineHeight: 1.55 }}>
              The Arena stays locked until Signing Day. Train here, then <b style={{ color: A }}>lock in the one track you'll drop</b> when
              the doors open. You can change your pick anytime before the day.
            </p>
          </section>
        )}

        {/* ① Beat */}
        <section ref={sBeat} style={{ ...panel, padding: 16, marginBottom: 22 }}>
          <div style={{ ...label, marginBottom: 10 }}>① The Beat</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => beatInputRef.current?.click()} style={accentBtn}>Load a beat</button>
            <input ref={beatInputRef} type="file" accept="audio/*" onChange={onBeatFile} style={{ display: 'none' }} />
            {beatLoaded ? (
              <>
                <span style={{ fontSize: 12, color: A, fontWeight: 700 }}>✓ {beatName}</span>
                <audio controls src={beatUrl} style={{ height: 34 }} />
                {bpmBusy ? (
                  <span style={{ fontSize: 12, color: MUTE }}>detecting BPM…</span>
                ) : beatBpm ? (
                  <span style={{ fontSize: 12, color: TEXT, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <b style={{ color: A, fontSize: 14 }}>{beatBpm} BPM</b>
                    <button onClick={() => setBeatBpm((v) => Math.round(v / 2))} style={{ ...ghostBtn, padding: '2px 7px' }} title="halve">½</button>
                    <button onClick={() => setBeatBpm((v) => Math.round(v * 2))} style={{ ...ghostBtn, padding: '2px 7px' }} title="double">×2</button>
                  </span>
                ) : null}
                <button onClick={clearBeat} style={{ ...ghostBtn, color: MUTE, borderColor: BORDER }}>Remove</button>
              </>
            ) : (
              <span style={{ fontSize: 12, color: MUTE }}>or go a cappella — load nothing.</span>
            )}
          </div>
          {beatLoaded && beatSegments.length > 0 && beatDur > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ ...label, marginBottom: 6 }}>Structure <span style={{ color: MUTE, textTransform: 'none', letterSpacing: 0 }}>(approx — drops/hooks vs verses)</span></div>
              <div style={{ display: 'flex', height: 34, borderRadius: 3, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
                {beatSegments.map((s, i) => {
                  const w = ((s.end - s.start) / beatDur) * 100;
                  const col = s.label === 'HOOK' ? A : s.label === 'VERSE' ? '#33507a' : '#3a352c';
                  return (
                    <div key={i} title={`${s.label}  ${fmtTime(s.start)}–${fmtTime(s.end)}`}
                      style={{ width: `${w}%`, minWidth: 2, background: col, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: i < beatSegments.length - 1 ? '1px solid #0009' : 'none' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: s.label === 'HOOK' ? '#000' : '#d8d2c4', whiteSpace: 'nowrap', overflow: 'hidden' }}>{w > 9 ? s.label : ''}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', ...label, marginTop: 4, color: MUTE }}>
                <span>0:00</span><span>{fmtTime(beatDur)}</span>
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, color: MUTE, marginTop: 10 }}>
            Curated beat library (drop on Signing Day) is coming. For now load your own — it plays while you record.
          </div>
        </section>

        {/* ② Write — BarCode */}
        <section ref={sWrite} style={{ ...panel, marginBottom: 22, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${BORDER}` }}>
            <div style={label}>② Write · <span style={{ color: A }}>powered by BARCODE</span></div>
            <a href={BARCODE_URL} target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: 'none' }}>Open full studio ↗</a>
          </div>
          <iframe title="BARCODE writing studio" src={BARCODE_URL}
            style={{ width: '100%', height: 560, border: 'none', display: 'block', background: '#000' }}
            allow="microphone; clipboard-read; clipboard-write" />
        </section>

        {/* ③ Record — the booth / mini-DAW */}
        <section ref={sRec} style={{ ...panel, padding: 16, marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
            <div style={label}>③ The Booth · Mini-DAW</div>
            <span style={{ fontSize: 11, color: beatLoaded ? A : MUTE }}>
              {beatLoaded ? `🎧 "${beatName}"${beatBpm ? ` · ${beatBpm} BPM` : ''} will play while you record — use headphones` : 'a cappella (no beat loaded)'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
            {!recording
              ? <button onClick={startRecording} style={{ ...accentBtn, background: '#c0392b', color: '#fff' }}>● Record{beatLoaded ? ' over beat' : ''}</button>
              : <button onClick={stopRecording} style={{ ...accentBtn, background: '#555', color: '#fff' }}>■ Stop</button>}
            {blob && <><span style={{ fontSize: 11, color: MUTE }}>your take:</span><audio controls src={URL.createObjectURL(blob)} style={{ height: 34 }} /></>}
          </div>
          <div style={{ fontSize: 11, color: MUTE, marginTop: 10 }}>Adlib + stack lanes and Master Now are coming. For now: beat + one vocal (your raw vocal is what the AI scores).</div>
        </section>

        {/* ④ Feedback */}
        <section ref={sFb} style={{ ...panel, padding: 16, marginBottom: 22 }}>
          <div style={{ ...label, marginBottom: 8 }}>④ Bars for feedback <span style={{ color: MUTE, textTransform: 'none', letterSpacing: 0 }}>(optional — paste from BarCode)</span></div>
          <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)}
            placeholder="Paste the bars you want the AI to read…"
            style={{ width: '100%', height: 90, background: '#0c0b09', color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 3, padding: 10, fontFamily: MONO, fontSize: 13, resize: 'vertical', outline: 'none' }} />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, color: MUTE, fontSize: 11, lineHeight: 1.5 }}>
            <input type="checkbox" checked={licenseOk} onChange={(e) => setLicenseOk(e.target.checked)} style={{ marginTop: 2, accentColor: A }} />
            <span>I keep ownership of my work, and grant TrapWorld a license to use content I create on the platform. (Final terms in the Terms of Service.)</span>
          </label>
          <button onClick={submit} disabled={!!busy} style={{ ...accentBtn, width: '100%', marginTop: 16, opacity: busy ? 0.5 : 1 }}>
            {busy ? `${busy}…` : 'Get private feedback'}
          </button>
        </section>

        {/* Trappa Keeper */}
        <div style={{ ...label, fontSize: 13, color: A, marginBottom: 12 }}>Your Trappa Keeper</div>
        {points.length > 1 && (
          <div style={{ ...panel, padding: 12, height: 180, marginBottom: 16 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <XAxis dataKey="date" tick={{ fill: MUTE, fontFamily: MONO, fontSize: 10 }} stroke={BORDER} />
                <YAxis domain={[0, 10]} tick={{ fill: MUTE, fontFamily: MONO, fontSize: 10 }} stroke={BORDER} />
                <Tooltip contentStyle={{ background: PANEL, border: `1px solid ${BORDER}`, fontFamily: MONO, fontSize: 12, color: TEXT }} />
                <Line type="monotone" dataKey="overall" stroke={A} strokeWidth={2} dot={{ fill: A, r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {takes.length === 0 && <div style={{ ...label, color: MUTE }}>No takes yet — load a beat, write, and record above.</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {takes.map((t) => (
            <div key={t.take_id} style={{ ...panel, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: MONO, fontSize: 13 }}>{t.title || (t.created_at || '').slice(0, 10)}</span>
                <span style={{ ...label, color: t.status === 'ready' ? A : t.status === 'failed' ? '#c0392b' : MUTE }}>{t.status}</span>
              </div>
              {t.status === 'ready' && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 10, fontSize: 12 }}>
                    {AXES.map((a) => (<span key={a} style={{ color: MUTE }}>{a.toUpperCase()} <b style={{ color: A, fontSize: 14 }}>{t[a]}</b></span>))}
                    <span style={{ color: MUTE }}>OVERALL <b style={{ color: A, fontSize: 14 }}>{t.overall}</b></span>
                  </div>
                  {t.feedback && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#c9c3b6', lineHeight: 1.6 }}>
                      {['strengths', 'fixes', 'drills'].map((k) => (<div key={k}><b style={{ color: A, textTransform: 'capitalize' }}>{k}:</b> {(t.feedback[k] || []).join('; ')}</div>))}
                    </div>
                  )}
                </>
              )}
              {t.status === 'failed' && <div style={{ marginTop: 8, color: '#c0392b', fontSize: 11 }}>Analysis failed: {t.error}</div>}
              {t.signing_day && <div style={{ ...label, color: A, marginTop: 10, letterSpacing: '0.12em' }}>★ Locked in for Signing Day</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {PRE_LAUNCH ? (
                  !t.signing_day && (
                    <button onClick={() => onLockIn(t.take_id)} style={{ ...ghostBtn, color: '#000', background: A, borderColor: A, fontWeight: 700 }}>Lock in for Signing Day</button>
                  )
                ) : (
                  <button onClick={() => onRelease(t.take_id)} disabled={!!t.released_performance_id} style={{ ...ghostBtn, opacity: t.released_performance_id ? 0.4 : 1 }}>
                    {t.released_performance_id ? 'Released' : 'Release to Arena'}
                  </button>
                )}
                <button onClick={() => onDelete(t.take_id)} style={{ ...ghostBtn, color: MUTE, borderColor: BORDER }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI demo pay wall — AI only; the rest of the room stays usable */}
      {showWall && (
        <div onClick={() => setShowWall(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ ...panel, maxWidth: 440, padding: 28, borderColor: `${A}66`, background: '#0c0b09', textAlign: 'center' }}>
            <div style={{ ...label, color: A, fontSize: 12 }}>AI Coach</div>
            <h2 style={{ fontFamily: MONO, color: TEXT, fontSize: 24, letterSpacing: '0.08em', margin: '10px 0 6px' }}>
              Your demo AI is up
            </h2>
            <p style={{ color: MUTE, fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
              You've used your free AI coaching time. Your room, beats, writing and recording all stay
              open — upgrade to bring the AI coach back.
            </p>
            <button onClick={() => alert("Upgrade is coming soon — you're on the early list.")}
              style={{ ...accentBtn, width: '100%', padding: '14px' }}>
              Upgrade to keep the AI coach
            </button>
            <button onClick={() => setShowWall(false)}
              style={{ ...ghostBtn, marginTop: 12, color: MUTE, borderColor: BORDER }}>
              Keep working without AI
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
